/**
 * useHeatmap — shared client cache of /api/heatmap.
 *
 * Returns per-city and per-category `{ vendors, members }` counts plus
 * a `heatFor(kind, name)` helper that picks a background/foreground
 * colour pair scaled to the max activity across the dataset. Zero
 * activity falls back to the tile's own theme colour (passed in).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

let cache = null;
let inflight = null;
const listeners = new Set();

async function fetchHeatmap(force = false) {
  if (cache && !force) return cache;
  if (inflight) return inflight;
  inflight = api.get("/heatmap")
    .then((r) => { cache = r.data; listeners.forEach((fn) => fn(cache)); return cache; })
    .catch(() => { cache = { cities: {}, categories: {} }; return cache; })
    .finally(() => { inflight = null; });
  return inflight;
}

export function useHeatmap() {
  const [data, setData] = useState(cache);
  useEffect(() => {
    if (!cache) fetchHeatmap();
    const fn = (d) => setData(d);
    listeners.add(fn);
    if (cache) setData(cache);
    return () => listeners.delete(fn);
  }, []);

  const maxes = useMemo(() => {
    if (!data) return { city: 1, category: 1 };
    let maxCity = 1;
    let maxCat = 1;
    Object.values(data.cities || {}).forEach((v) => {
      const total = (v.vendors || 0) + (v.members || 0);
      if (total > maxCity) maxCity = total;
    });
    Object.values(data.categories || {}).forEach((v) => {
      const total = (v.vendors || 0) + (v.members || 0);
      if (total > maxCat) maxCat = total;
    });
    return { city: maxCity, category: maxCat };
  }, [data]);

  /**
   * Given kind ("city" | "category"), the entity name and a fallback
   * theme colour, return `{ bg, fg, tone, count }` for the tile icon.
   * Zero activity → theme colour fallback.
   */
  const heatFor = (kind, name, themeColor) => {
    const bucket = data?.[kind === "city" ? "cities" : "categories"]?.[name];
    const vendors = bucket?.vendors || 0;
    const members = bucket?.members || 0;
    const total = vendors + members;
    if (!bucket || total === 0) {
      return {
        bg: `${themeColor}22`,
        fg: themeColor,
        tone: "cold",
        count: 0,
        vendors: 0,
        members: 0,
      };
    }
    const max = kind === "city" ? maxes.city : maxes.category;
    const ratio = Math.min(1, total / Math.max(1, max));
    // Heat scale (blue→green→amber→red) — passes WCAG on white/dark bg.
    let tone = "warm";
    let bg = "#fde68a";
    let fg = "#78350f";
    if (ratio <= 0.15) { tone = "cool"; bg = "#dbeafe"; fg = "#1e40af"; }
    else if (ratio <= 0.35) { tone = "mild"; bg = "#d1fae5"; fg = "#065f46"; }
    else if (ratio <= 0.65) { tone = "warm"; bg = "#fde68a"; fg = "#78350f"; }
    else if (ratio <= 0.85) { tone = "hot"; bg = "#fed7aa"; fg = "#7c2d12"; }
    else { tone = "blazing"; bg = "#fecaca"; fg = "#7f1d1d"; }
    return { bg, fg, tone, count: total, vendors, members };
  };

  return { data, heatFor, refresh: () => fetchHeatmap(true) };
}
